<template>
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up" style="width:700px">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <h2 class="rk-modal-title">{{ quarterLabel(quarter) }} → {{ quarterLabel(next) }}</h2>

            <p v-if="!loaded" class="rk-intro">Loading&hellip;</p>

            <!-- Step 1: decide what happens to every unfinished rock -->
            <template v-else-if="step == 1">
                <p class="rk-intro">Step 1 of 2: close out {{ quarterLabel(quarter) }}. Decide what happens to every rock that isn't done.<template v-if="doneCount"> {{ doneCount }} already done.</template></p>
                <div v-for="r in open" :key="r.ID" class="rk-rw">
                    <span class="rk-own" :title="ownerName(r.OwnerEmployeeID)">{{ initials(ownerName(r.OwnerEmployeeID)) }}</span>
                    <div class="rk-rw-name">
                        <b>{{ r.Name }}</b>
                        <span>{{ r.Milestones.filter(m => m.Done).length }}/{{ r.Milestones.length }} milestones · {{ r.Status == 'off' ? 'off track' : 'on track' }}</span>
                    </div>
                    <div class="rk-seg">
                        <button v-for="d in DECISIONS" :key="d.value" type="button" :class="decisions[r.ID] == d.value ? 'on' : ''" @click="decisions[r.ID] = d.value">{{ d.label }}</button>
                    </div>
                </div>
                <div v-if="!open.length" class="rk-clear">Every {{ quarterLabel(quarter) }} rock you can update is already done or decided.</div>
                <p v-if="decidedCount" class="rk-hint" style="margin-top:10px">
                    {{ decidedCount }} decided: {{ countOf('done') }} done, {{ countOf('carry') }} carried forward, {{ countOf('drop') }} let go.
                    <a class="rk-link" @click.prevent="decisions = {}">Undo All</a>
                </p>
            </template>

            <!-- Step 2: set next quarter's rocks -->
            <template v-else>
                <p class="rk-intro">Step 2 of 2: set {{ quarterLabel(next) }} rocks, 3 to 7 per team. Carried rocks are already in.</p>
                <div v-for="r in nextRocks" :key="r.ID" class="rk-rwn">
                    <span class="rk-own" :title="ownerName(r.OwnerEmployeeID)">{{ initials(ownerName(r.OwnerEmployeeID)) }}</span>
                    <b>{{ r.Name }}</b>
                    <span v-if="r.CarriedFromRockID" class="rk-chip">Carried</span>
                    <span class="rk-chip">{{ r.Team ? teamLabel(r.Team) : 'No team' }}</span>
                </div>
                <p v-if="!nextRocks.length" class="rk-hint">No rocks yet.</p>
                <form v-if="teams.length" class="rk-rwadd" @submit.prevent="addRock()">
                    <input type="text" v-model="newRock.Name" :placeholder="'New rock for ' + quarterLabel(next)" ref="newRockInput" />
                    <select v-model="newRock.OwnerEmployeeID" aria-label="Owner">
                        <option v-for="e in employees" :key="e.ID" :value="e.ID">{{ e.Name }}</option>
                    </select>
                    <select v-model="newRock.Team" aria-label="Team">
                        <option v-for="t in teams" :key="t" :value="t">{{ teamLabel(t) }}</option>
                    </select>
                    <button class="btn" :class="adding ? 'disable-button' : ''">Add</button>
                </form>
                <p class="rk-hint" style="margin-top:8px">Add milestones to each rock from the Rocks tab once the list is final.</p>
            </template>

            <div class="rk-actions">
                <template v-if="step == 1">
                    <div class="btn-light" @click="$emit('close')">Save for Later</div>
                    <span style="flex:1"></span>
                    <span v-if="undecided" class="rk-hint">Decide {{ undecided }} more first</span>
                    <div class="btn" :class="undecided || applying ? 'disable-button' : ''" @click="applyDecisions()">Next: Set {{ quarterLabel(next) }} Rocks</div>
                </template>
                <template v-else>
                    <span style="flex:1"></span>
                    <div class="btn" @click="$emit('finish', next)">Done, Show {{ quarterLabel(next) }}</div>
                </template>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import { quarterLabel, nextQuarter, isRockOwner } from '@/rocks';
import { initials } from '@/scoreboard';

export default defineComponent({
    name: 'RockWeekModal',
    // quarter: the quarter being closed out (the current one). teams: the teams you can put a rock on;
    // defaultTeam: the one a new rock starts on.
    props: [ 'quarter', 'employees', 'teams', 'defaultTeam', 'myEmployeeID' ],
    emits: [ 'close', 'finish', 'changed' ],
    data() {
        return {
            DECISIONS: [ { value: 'done', label: 'Done' }, { value: 'carry', label: 'Carry Forward' }, { value: 'drop', label: 'Let Go' } ],
            loaded: false,
            step: 1,
            rocks: [] as any[],
            nextRocks: [] as any[],
            decisions: {} as any,
            applying: false,
            adding: false,
            newRock: {} as any
        }
    },
    async created() {
        this.resetNewRock();
        await this.loadQuarter();
        this.loaded = true;
    },
    computed: {
        next(): string {
            return nextQuarter(this.quarter);
        },
        // Unfinished rocks this person can decide on: any for Team Hub Admins, else the ones they own (rocks.ts canUpdate)
        open(): any[] {
            const isAdmin = this.$store.state.user.teamHubRole == 'Admin';
            return this.rocks.filter((r: any) => r.CanUpdate && (isAdmin || isRockOwner(r, this.myEmployeeID))
                && r.Status != 'done' && r.Status != 'notdone' && !r.CarriedToQuarter);
        },
        doneCount(): number {
            return this.rocks.filter((r: any) => r.Status == 'done').length;
        },
        decidedCount(): number {
            return this.open.filter((r: any) => this.decisions[r.ID]).length;
        },
        undecided(): number {
            return this.open.length - this.decidedCount;
        },
    },
    methods: {
        quarterLabel,
        initials,
        ownerName(id: any): string {
            return this.employees.find((e: any) => e.ID == id)?.Name || 'No owner';
        },
        countOf(decision: string): number {
            return this.open.filter((r: any) => this.decisions[r.ID] == decision).length;
        },
        resetNewRock() {
            this.newRock = { Name: '', OwnerEmployeeID: this.myEmployeeID || null, Team: this.defaultTeam || this.teams[0] || null };
        },
        async loadQuarter() {
            await axios.get(`${this.$store.state.apiUrl}/api/rocks`, { params: { quarter: this.quarter } })
                .then(resp => {
                    this.rocks = resp.data.Rocks;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async loadNext() {
            await axios.get(`${this.$store.state.apiUrl}/api/rocks`, { params: { quarter: this.next } })
                .then(resp => {
                    this.nextRocks = resp.data.Rocks;
                })
                .catch(e => {
                    console.log(e);
                })
        },
        async applyDecisions() {
            if(this.undecided || this.applying) {
                return;
            }
            this.applying = true;
            await axios.post(`${this.$store.state.apiUrl}/api/rocks/closeout`, {
                Quarter: this.quarter,
                Decisions: this.open.map((r: any) => ({ ID: r.ID, Decision: this.decisions[r.ID] }))
            })
                .then(async () => {
                    this.$emit('changed');
                    await this.loadNext();
                    this.step = 2;
                    this.$nextTick(() => (this.$refs.newRockInput as HTMLInputElement)?.focus());
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })
            this.applying = false;
        },
        async addRock() {
            if(!this.newRock.Name.trim() || this.adding) {
                (this.$refs.newRockInput as HTMLInputElement)?.focus();
                return;
            }
            this.adding = true;
            await axios.post(`${this.$store.state.apiUrl}/api/rocks/rock`, {
                ...this.newRock,
                Quarter: this.next,
                Status: 'on',
                Milestones: []
            })
                .then(async () => {
                    await this.loadNext();
                    this.newRock.Name = '';
                    (this.$refs.newRockInput as HTMLInputElement)?.focus();
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error');
                })
            this.adding = false;
        }
    }
});
</script>

<style scoped>
.rk-modal-title { margin: 0 0 6px; font-size: 20px; }
.rk-intro { font-size: 13.5px; color: var(--font-dark-gray); margin: 0 0 12px; }
.rk-hint { font-weight: normal; color: var(--font-mid-gray); font-size: 12px; }
.rk-link { color: var(--primary-color); text-decoration: underline; cursor: pointer; margin-left: 6px; }
.rk-own { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; background: #024E73; color: #fff; font-weight: 700; font-size: 10px; flex-shrink: 0; }
.rk-rw { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-top: 1px solid var(--mid-gray); flex-wrap: wrap; }
.rk-rw-name { flex: 1; min-width: 200px; display: flex; flex-direction: column; gap: 2px; }
.rk-rw-name b { font-size: 14px; }
.rk-rw-name span { font-size: 12px; color: var(--font-mid-gray); }
.rk-seg { display: inline-flex; background: #fff; border: 1px solid var(--mid-gray); border-radius: 8px; padding: 2px; }
.rk-seg button { background: none; border: none; font-family: inherit; font-size: 12.5px; color: var(--font-dark-gray); padding: 6px 11px; border-radius: 6px; cursor: pointer; white-space: nowrap; }
.rk-seg button.on { background: var(--primary-color); color: #fff; font-weight: 600; }
.rk-clear { padding: 14px; border-radius: 10px; background: #E3F4E8; color: #2E7D46; font-weight: 600; font-size: 13.5px; }
.rk-rwn { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-top: 1px solid var(--mid-gray); font-size: 14px; }
.rk-rwn b { flex: 1; min-width: 0; }
.rk-chip { display: inline-flex; align-items: center; background: var(--light-gray); border-radius: 10px; padding: 2px 8px; font-size: 11.5px; font-weight: 600; white-space: nowrap; }
.rk-rwadd { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-top: 12px; }
.rk-rwadd input, .rk-rwadd select { margin: 0 !important; }
.rk-rwadd input { flex: 1; min-width: 200px; }
.rk-rwadd select { width: auto; max-width: 170px; }
.rk-rwadd .btn { margin: 0; padding: 8px 16px; }
.rk-actions { display: flex; align-items: center; gap: 10px; margin-top: 24px; }
.rk-actions .btn, .rk-actions .btn-light { margin: 0; padding: 8px 18px; }
</style>
